import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { BarChart3, Building2, CircleDollarSign, CreditCard, FileClock, Gift, ScrollText, ShieldCheck, Sparkles, Users, Workflow } from 'lucide-react';

import { BrandMark } from '@tala/design-tokens/brand/BrandMark';

import { NavItem } from '@/components/NavItem';
import { logout } from '@/lib/adminApi';
import { canManageSkills, canManageSystemPrompts, canReadSpend, canReadUsageReports, canReadUserReports, canReadUserWorkflows } from '@/lib/roles';
import { ME_QUERY_KEY, useMe } from '@/lib/useMe';

/**
 * Phase 1+2 destinations (support-admin-proposal.md §6): user search/lookup,
 * content history, payment-token deletion, the audit log, and (Phase 2)
 * credit grants / manual plan activation.
 *
 * NAV DECISION (this integration merge, 869f084w5), FINAL after all four
 * feature branches: four items — Users, Organizations, Payments, Audit log
 * — ALL live, no disabled placeholders left. "Content" is NOT a separate
 * nav item: content history is reached PER USER (§1.4, a user's own Content
 * tab on /users/:userId), folded into the Users card. "Organizations" is
 * the Phase 2 credit-grant / plan-activation lookup (869f0ev2j/869f0ev2r),
 * kept separate from "Users" (real user search/lookup, 869f0ev1r/869f0ev2a)
 * since they are different admin workflows against different tables.
 * "Payments" (869f0ev24/869f0ev30) is its own lookup too — payment-token
 * deletion needs the payments-service view, not the product profile.
 *
 * "Grants log" (869f084w5 §4) joined them afterwards and sits ABOVE "Audit
 * log" deliberately: it is the screen support actually opens ("what did we
 * give this customer, and until when"), while the audit log is the wider,
 * rawer record underneath it. Both read the same table — the grants panel is
 * a projection of it, not a second store.
 */
const PHASE_1_NAV = [
  { icon: Users, label: 'Users', to: '/users' },
  { icon: Building2, label: 'Organizations', to: '/organizations' },
  { icon: CreditCard, label: 'Payments', to: '/payments' },
  { icon: Gift, label: 'Grants log', to: '/grants' },
  { icon: FileClock, label: 'Audit log', to: '/audit' },
  { icon: ShieldCheck, label: 'Admin accounts', to: '/admin-accounts' },
];

/**
 * Superadmin only (the owner's line, 19.09.2026, ClickUp 869f4c8v4):
 * analytics about users is not support's. Appended to the nav for that one
 * role; the route itself and the API refuse everyone else.
 */
const SUPERADMIN_NAV = [{ icon: BarChart3, label: 'Reports', to: '/reports' }];
const SYSTEM_PROMPTS_NAV = { icon: ScrollText, label: 'System prompts', to: '/system-prompts' };
const SKILLS_NAV = { icon: Sparkles, label: 'Skills', to: '/skills' };
const WORKFLOWS_NAV = { icon: Workflow, label: 'User workflows', to: '/workflows' };

/** What generations cost us (ClickUp 1amz0xe2evr): every console role, `viewer` and up. */
const SPEND_NAV = [{ icon: CircleDollarSign, label: 'Spend', to: '/spend' }];

export function AuthenticatedShell() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [navigationOpen, setNavigationOpen] = useState(false);
  const navigationToggle = useRef<HTMLButtonElement>(null);

  useEffect(() => { setNavigationOpen(false); }, [location.key]);

  function closeNavigation() {
    if (!navigationOpen) return;
    setNavigationOpen(false);
    navigationToggle.current?.focus();
  }

  // RequireSession only renders this shell once GET /api/admin-auth/me
  // resolved to a real session, so `me` is non-null in practice — but the
  // query can still refetch and briefly go stale, so this guards the render
  // rather than assuming.
  if (!me) return null;

  async function handleLogout() {
    await logout();
    queryClient.setQueryData(ME_QUERY_KEY, null);
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex min-h-screen flex-col bg-background md:flex-row">
      <nav
        aria-label="Admin navigation"
        className="shrink-0 border-b border-border p-4 md:w-56 md:border-b-0 md:border-r"
        onKeyDown={(event) => {
          if (event.key === 'Escape' && navigationOpen) {
            event.preventDefault();
            closeNavigation();
          }
        }}
      >
        {/* The product's mark, from @tala/design-tokens — the only package
            this app is allowed to import from, which is exactly why the mark
            lives there. Before 20.09.2026 the console identified itself with
            four words of type and nothing else, so it read like a different
            product to the people who use it all day. The glyph takes its ink
            from `currentColor`, so the heading's own colour re-themes it. */}
        <div className="flex items-center gap-2 text-h3 font-semibold text-fg-primary md:mb-6">
          <BrandMark size="md" className="shrink-0" />
          <span>Admin</span>
          <button
            ref={navigationToggle}
            type="button"
            aria-expanded={navigationOpen}
            aria-controls="admin-navigation"
            onClick={() => setNavigationOpen((open) => !open)}
            className="ml-auto min-h-tap min-w-tap rounded-card px-3 py-2 text-body font-medium text-fg-primary hover:bg-state-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary md:hidden"
          >
            Menu
          </button>
        </div>
        <div id="admin-navigation" className={`${navigationOpen ? 'flex' : 'hidden'} mt-4 flex-col gap-1 md:mt-0 md:flex`}>
          {[
            ...PHASE_1_NAV,
            ...(canReadSpend(me.role) ? SPEND_NAV : []),
            ...(canReadUserReports(me.role) || canReadUsageReports(me.role, me.capabilities) ? SUPERADMIN_NAV : []),
            ...(canReadUserWorkflows(me.role, me.capabilities) ? [WORKFLOWS_NAV] : []),
            ...(canManageSystemPrompts(me.role) ? [SYSTEM_PROMPTS_NAV] : []),
            ...(canManageSkills(me.role) ? [SKILLS_NAV] : []),
          ].map(({ icon, label, to }) => (
            <NavItem key={label} icon={icon} label={label} to={to} disabled={!to} onClick={closeNavigation} />
          ))}
        </div>
      </nav>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3 md:px-6">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <span className="min-w-0 break-words text-body text-fg-primary [overflow-wrap:anywhere]">{me.email}</span>
            <span className="rounded-full bg-surface-brand-subtle px-2 py-0.5 text-micro font-medium uppercase tracking-wide text-fg-secondary">
              {me.role}
            </span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="rounded-card px-3 py-1.5 text-meta font-medium text-fg-secondary hover:bg-state-hover"
          >
            Logout
          </button>
        </header>
        <main className="min-w-0 flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
